import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Spinner, Alert, Table, Badge, Form, Row, Col } from "react-bootstrap";
import { FaLock, FaLockOpen, FaHeartbeat, FaSync, FaShieldAlt } from "react-icons/fa";
import firebaseApp from "../../../firebase/credenciales";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import {
  evaluarSaludContable,
  ejecutarCierreContable,
  ejecutarReaperturaContable
} from "../../../services/contabilidad/cierreContableService";
import { CONT_COLLECTIONS } from "../../../services/contabilidad/collections";
import { periodoActual } from "../utils";

const db = getFirestore(firebaseApp);

const BTN = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const CierresContables = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [periodo, setPeriodo] = useState(periodoActual());
  const [loading, setLoading] = useState(false);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  const [estadoPeriodo, setEstadoPeriodo] = useState("ABIERTO");
  const [cierreData, setCierreData] = useState(null);
  const [saludResult, setSaludResult] = useState(null);

  const consultarEstado = useCallback(async () => {
    if (!rucEmpresa || !periodo) return;
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      setSaludResult(null);

      const docId = `${rucEmpresa}_${periodo}_GENERAL`;
      const docRef = doc(db, CONT_COLLECTIONS.SIRE_CIERRES, docId);
      const snap = await getDoc(docRef);

      if (snap.exists() && snap.data().estado === "CERRADO") {
        setEstadoPeriodo("CERRADO");
        setCierreData(snap.data());
      } else {
        setEstadoPeriodo("ABIERTO");
        setCierreData(null);
      }
    } catch (e) {
      console.error(e);
      setError("No se pudo obtener el estado del período contable.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa, periodo]);

  useEffect(() => {
    consultarEstado();
  }, [consultarEstado]);

  const testSaludContable = async () => {
    try {
      setCheckingHealth(true);
      setError("");
      const res = await evaluarSaludContable({ rucEmpresa, periodo });
      setSaludResult(res);
    } catch (e) {
      console.error(e);
      setError("Error evaluando la salud contable: " + e.message);
    } finally {
      setCheckingHealth(false);
    }
  };

  const realizarCierre = async () => {
    if (!window.confirm("¿Está seguro de realizar el cierre definitivo de este periodo contable? Se bloquearán las modificaciones de asientos.")) return;
    try {
      setLoading(true);
      setError("");
      await ejecutarCierreContable({
        rucEmpresa,
        periodo,
        usuario: usuario?.email || "ADMINISTRADOR",
        motivo: "Cierre mensual contable unificado"
      });
      setSuccess(`Periodo ${periodo} cerrado de forma definitiva.`);
      await consultarEstado();
    } catch (e) {
      console.error(e);
      setError("Error al ejecutar el cierre: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const realizarReapertura = async () => {
    if (!window.confirm("¿Está seguro de reabrir este periodo contable? Se desbloquearán las modificaciones.")) return;
    try {
      setLoading(true);
      setError("");
      await ejecutarReaperturaContable({
        rucEmpresa,
        periodo,
        usuario: usuario?.email || "ADMINISTRADOR",
        motivo: "Reapertura de periodo contable",
        rol: usuario?.rol || "admin"
      });
      setSuccess(`Periodo ${periodo} reabierto con éxito.`);
      await consultarEstado();
    } catch (e) {
      console.error(e);
      setError("Error al reabrir el periodo: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible className="border-0 shadow-sm">{success}</Alert>}

      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center g-3">
            <Col md={1} className="text-center">
              <div className="rounded-3 p-3 d-inline-block bg-light text-secondary">
                <FaShieldAlt size={24} />
              </div>
            </Col>
            <Col md={4}>
              <h5 className="mb-1 fw-semibold">Cierres y Períodos Contables</h5>
              <p className="text-muted mb-0 small">Bloqueo y desbloqueo de períodos fiscales para auditoría.</p>
            </Col>
            <Col md={3}>
              <Form.Control type="month" className="fw-bold" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
            </Col>
            <Col md={4} className="text-end d-flex gap-2 justify-content-end">
              <Button variant="outline-primary" className="rounded-pill btn-sm fw-bold" onClick={testSaludContable} disabled={loading || checkingHealth}>
                {checkingHealth ? <Spinner animation="border" size="sm" /> : <><FaHeartbeat /> Evaluar Salud</>}
              </Button>
              {estadoPeriodo === "ABIERTO" ? (
                <Button className="rounded-pill px-3 btn-sm border-0" style={BTN} onClick={realizarCierre} disabled={loading || (saludResult && saludResult.severeCount > 0)}>
                  <FaLock /> Cerrar Período
                </Button>
              ) : (
                <Button variant="warning" className="rounded-pill px-3 btn-sm text-dark fw-bold" onClick={realizarReapertura} disabled={loading}>
                  <FaLockOpen /> Reabrir Período
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Estado del Periodo Actual */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4 d-flex align-items-center justify-content-between">
          <div>
            <h6 className="text-muted small mb-1">Estado de Periodo {periodo}</h6>
            <div className="d-flex align-items-center gap-2">
              <h4 className="fw-bold mb-0">{estadoPeriodo}</h4>
              <Badge bg={estadoPeriodo === "ABIERTO" ? "success" : "danger"} className="rounded-pill px-3 py-1">
                {estadoPeriodo === "ABIERTO" ? "Listo para transacciones" : "Bloqueado para edición"}
              </Badge>
            </div>
          </div>
          {cierreData && (
            <div className="text-end text-muted small">
              <div>Cerrado por: <strong>{cierreData.cerrado_por}</strong></div>
              <div>Fecha: {cierreData.cerrado_at?.toDate ? cierreData.cerrado_at.toDate().toLocaleString() : "—"}</div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Resultado de Salud Contable */}
      {saludResult && (
        <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <Card.Header className="bg-light border-0 py-3 px-4 fw-bold text-dark">
            Resultado de Auditoría de Salud Contable (Pre-cierre)
          </Card.Header>
          <Card.Body className="p-4">
            <div className="mb-3 d-flex align-items-center gap-3">
              <div>Estado de Cierre: <strong>{saludResult.status}</strong></div>
              <div>Inconsistencias Críticas: <Badge bg={saludResult.severeCount > 0 ? "danger" : "success"}>{saludResult.severeCount}</Badge></div>
            </div>

            {saludResult.findings.length === 0 ? (
              <Alert variant="success" className="border-0 py-3 mb-0">
                ✓ No se detectaron inconsistencias. El periodo contable está apto para cierre.
              </Alert>
            ) : (
              <Table hover responsive className="mb-0 border">
                <thead className="table-light">
                  <tr>
                    <th>Severidad</th>
                    <th>Módulo</th>
                    <th>Hallazgo / Mensaje</th>
                  </tr>
                </thead>
                <tbody>
                  {saludResult.findings.map((f, idx) => (
                    <tr key={idx}>
                      <td>
                        <Badge bg={f.severidad === "CRITICA" || f.severidad === "ALTA" ? "danger" : "warning"} className="px-2 py-1">
                          {f.severidad}
                        </Badge>
                      </td>
                      <td className="fw-bold">{f.modulo}</td>
                      <td className="text-muted">{f.mensaje}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default CierresContables;
